import { CONFIRM_NOTIFICATION_THEME } from '../../Modals/Notifications/ConfirmNotification';
import { AMPLITUDE_EVENTS, trackAnalytics } from '../../Tracking/baglessAnalytics';

export const AI_INTERACTION_CONSENT_STORAGE_KEY = 'bagless_ai_interaction_consent';
export const AI_INTERACTION_CONSENT_DONT_SHOW_ID = 'ai-interaction-consent-dont-show';

const CONSENT_ACCEPT = 'accept';
const CONSENT_DECLINE = 'decline';

let consentDialogOpen = false;

function t(key, fallback) {
    if (typeof $trans === 'function') {
        return $trans(key, {}, fallback);
    }
    return fallback;
}

function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

export function getRememberedAiInteractionConsent() {
    try {
        const value = localStorage.getItem(AI_INTERACTION_CONSENT_STORAGE_KEY);
        if (value === CONSENT_ACCEPT || value === CONSENT_DECLINE) return value;
    } catch (e) {
        /* ignore */
    }
    return null;
}

export function rememberAiInteractionConsent(decision) {
    if (decision !== CONSENT_ACCEPT && decision !== CONSENT_DECLINE) return;
    try {
        localStorage.setItem(AI_INTERACTION_CONSENT_STORAGE_KEY, decision);
    } catch (e) {
        /* ignore */
    }
}

function isDontShowAgainChecked() {
    const checkbox = document.getElementById(AI_INTERACTION_CONSENT_DONT_SHOW_ID);
    return !!(checkbox && checkbox.checked);
}

function buildConsentMessageHtml() {
    const message = escapeHtml(t(
        'ai-interaction-consent-message',
        'You are about to interact with an AI system. Do you want to continue?'
    ));
    const dontShow = escapeHtml(t('dont-show-again-text', "Don't show this again"));
    return `
        <div class="ai-interaction-consent">
            <p class="ai-interaction-consent__message">${message}</p>
            <div class="ai-interaction-consent__remember">
                <div class="customCheckbox">
                    <input type="checkbox" id="${AI_INTERACTION_CONSENT_DONT_SHOW_ID}" />
                    <label for="${AI_INTERACTION_CONSENT_DONT_SHOW_ID}">
                        <span class="material-icons-outlined md-18 box_uncheck">check_box_outline_blank</span>
                        <span class="material-icons-outlined md-18 box_check">check_box</span>
                    </label>
                </div>
                <label class="ai-interaction-consent__remember-label" for="${AI_INTERACTION_CONSENT_DONT_SHOW_ID}">${dontShow}</label>
            </div>
        </div>
    `;
}

/**
 * Evaluate AI interaction consent before VAPI start.
 * Returns true only when the user accepts (or previously accepted with "Don't show this again").
 * The dialog is never shown while already open; a second start attempt is ignored.
 */
export function requestAiInteractionConsent(model) {
    const remembered = getRememberedAiInteractionConsent();
    if (remembered === CONSENT_ACCEPT) return Promise.resolve(true);
    if (remembered === CONSENT_DECLINE) return Promise.resolve(false);
    if (consentDialogOpen) return Promise.resolve(false);
    if (typeof model?.confirm !== 'function') return Promise.resolve(false);

    consentDialogOpen = true;

    return new Promise((resolve) => {
        let settled = false;
        const finish = (accepted) => {
            if (settled) return;
            settled = true;
            consentDialogOpen = false;
            if (isDontShowAgainChecked()) {
                rememberAiInteractionConsent(accepted ? CONSENT_ACCEPT : CONSENT_DECLINE);
            }
            resolve(!!accepted);
        };

        model.confirm(
            buildConsentMessageHtml(),
            () => finish(true),
            {
                id: 'ai-interaction-consent',
                header: true,
                title: t('ai-interaction-consent-title', 'AI interaction'),
                confirmBtnTxt: t('ai-interaction-consent-accept', 'Accept'),
                cancelBtnTxt: t('ai-interaction-consent-decline', 'Decline'),
                xBtn: false,
                dimmerClose: false,
                position: 'middle',
                modalTheme: CONFIRM_NOTIFICATION_THEME.DARK,
            },
            () => {
                if (!model?.editMode) {
                    model?.view?._executeSceneEventActions?.('consentDeclined');
                    trackAnalytics(model, AMPLITUDE_EVENTS.DECLINED_CONSENT_DIALOGUE, {
                        consent_type: 'ai_interaction',
                    });
                }
                finish(false);
            }
        );
    });
}
